// ─── Feuille : filtrer et trier la vue Mois ───────────────────────
// Deux onglets (enveloppes / tri), comme la feuille de filtres du calendrier.
// Brouillon local : rien n'est appliqué tant qu'on n'a pas validé — on peut
// donc explorer les cases sans faire clignoter la liste derrière.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Feuille, useUI, useT } from '@mop/core';
import { emojiBudget } from './data/categories';
import { TRIS } from './services/filtres';

export default function FeuilleFiltresMois({
  visible, ongletInitial = 'budgets', budgets = [], budgetIds = [], tri = 'statut',
  onAppliquer, onFermer,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [onglet, setOnglet] = useState('budgets');
  const [selBudgets, setSelBudgets] = useState([]);
  const [selTri, setSelTri] = useState('statut');

  useEffect(() => {
    if (!visible) return;
    setOnglet(ongletInitial);
    setSelBudgets(Array.isArray(budgetIds) ? budgetIds : []);
    setSelTri(tri || 'statut');
  }, [visible, ongletInitial, budgetIds, tri]);

  const basculer = (id) => setSelBudgets(l => (l.includes(id) ? l.filter(x => x !== id) : [...l, id]));
  const appliquer = () => { onAppliquer?.({ budgetIds: selBudgets, tri: selTri }); onFermer?.(); };
  // « Tout » = aucune restriction (liste vide), pas « tout coché » : c'est le
  // même résultat mais l'intention est plus claire à relire.
  const tout = () => setSelBudgets([]);

  return (
    <Feuille visible={visible} titre={t('finances.filtrerTrier')} emoji="🔎" hauteur={0.75} onFermer={onFermer}
      action={{ icone: '✓', label: t('commun.valider'), onPress: appliquer }}>

      <View style={s.onglets}>
        {[{ id: 'budgets', lbl: t('famille.t.finances') }, { id: 'tri', lbl: t('finances.tri') }].map(o => (
          <TouchableOpacity key={o.id} style={[s.onglet, onglet === o.id && s.ongletOn]}
            activeOpacity={0.7} onPress={() => setOnglet(o.id)}>
            <Text style={[s.ongletTxt, onglet === o.id && s.ongletTxtOn]}>{o.lbl}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {onglet === 'budgets' ? (
        <>
          <TouchableOpacity style={[s.item, !selBudgets.length && s.itemOn]} activeOpacity={0.7} onPress={tout}>
            <Text style={s.coche}>{!selBudgets.length ? '☑' : '☐'}</Text>
            <Text style={[s.itemTxt, !selBudgets.length && s.itemTxtOn]}>{t('finances.tousBudgets')}</Text>
          </TouchableOpacity>
          {budgets.map(b => {
            const on = selBudgets.includes(b.id);
            return (
              <TouchableOpacity key={b.id} style={[s.item, on && s.itemOn]} activeOpacity={0.7} onPress={() => basculer(b.id)}>
                <Text style={s.coche}>{on ? '☑' : '☐'}</Text>
                <Text style={{ fontSize: 17 }}>{emojiBudget(b)}</Text>
                <Text style={[s.itemTxt, on && s.itemTxtOn]} numberOfLines={1}>{b.nom}</Text>
              </TouchableOpacity>
            );
          })}
          {/* Les lignes sans enveloppe existent (budgetId null) : sans cette
              entrée, elles seraient introuvables dès qu'un filtre est posé. */}
          <TouchableOpacity style={[s.item, selBudgets.includes(null) && s.itemOn]} activeOpacity={0.7}
            onPress={() => basculer(null)}>
            <Text style={s.coche}>{selBudgets.includes(null) ? '☑' : '☐'}</Text>
            <Text style={{ fontSize: 17 }}>∅</Text>
            <Text style={[s.itemTxt, selBudgets.includes(null) && s.itemTxtOn]}>{t('budget.sansBudget')}</Text>
          </TouchableOpacity>
        </>
      ) : (
        TRIS.map(x => (
          <TouchableOpacity key={x.id} style={[s.item, selTri === x.id && s.itemOn]}
            activeOpacity={0.7} onPress={() => setSelTri(x.id)}>
            <Text style={s.coche}>{selTri === x.id ? '◉' : '○'}</Text>
            <Text style={[s.itemTxt, selTri === x.id && s.itemTxtOn]}>{t(x.cle)}</Text>
          </TouchableOpacity>
        ))
      )}
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  onglets: { flexDirection: 'row', gap: 8, marginBottom: 12 },
  onglet: { flex: 1, paddingVertical: 10, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, backgroundColor: UI.card, alignItems: 'center' },
  ongletOn: { borderColor: UI.accent, borderWidth: 1.5 },
  ongletTxt: { fontSize: 13.5, color: UI.muted, fontWeight: '700' },
  ongletTxtOn: { color: UI.accent },
  item: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 12, paddingHorizontal: 12, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, backgroundColor: UI.card, marginBottom: 8 },
  itemOn: { borderColor: UI.accent },
  coche: { fontSize: 16, color: UI.accent, width: 20 },
  itemTxt: { flex: 1, fontSize: 14.5, color: UI.ink },
  itemTxtOn: { fontWeight: '800' },
});
